import React, { useEffect } from 'react';
import { Box, Typography, Button, Stack } from '@curves/ui-base';
import { useHistory, useLocation } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import { MEMBER_REDEEM_ROUTER, MEMBER_REDEEM_PRODUCT_SELECT_ROUTER } from '@Router/routerPath';
import { RedeemStepper } from '@Local/Redeem/Components';
import useRedeemCart from '@Local/Redeem/hooks/useRedeemCart';
import useMemberRedeemApi from '@Apis/useMemberRedeemApi';

// 前端 cart map 轉為後端送出格式：{ redeemResults: [{ itemID, quantity }] }
// 僅送出目前活動仍存在的品項，避免草稿殘留已下架商品（畫面看不到卻被送出）
const toRedeemPayload = (cart = {}, products = []) => {
    const validItemIDs = new Set(products.map((p) => p.itemID));
    const redeemResults = Object.entries(cart)
        .filter(([itemID, quantity]) => quantity > 0 && validItemIDs.has(Number(itemID)))
        .map(([itemID, quantity]) => ({ itemID: Number(itemID), quantity }));

    return { redeemResults };
};

/**
 * 兌換確認頁。唯讀核對品項與點數，送出後導回列表頁。
 * 入口：RedeemProductSelectPage → handleNext（location.state: { cart, products, redeemablePoints, currentPoints }）
 */
const RedeemConfirmPage = () => {
    const { setHeaderType, setFooterType, setThemeType, setHeaderContent } = useMainLayoutContext();
    const history = useHistory();
    const location = useLocation();
    const {
        cart = {},
        products = [],
        redeemablePoints = 0,
        currentPoints = 0,
        canRedeem = false,
        redeemStart = null,
        redeemEnd = null,
    } = location.state || {};
    const { postRedeemCartApi } = useMemberRedeemApi();

    // 功能：返回選品頁繼續編輯，草稿改回當 sourceCart 帶下去
    const handleBack = () => {
        history.replace(MEMBER_REDEEM_PRODUCT_SELECT_ROUTER, {
            sourceCart: cart,
            products,
            redeemablePoints,
            currentPoints,
            canRedeem,
            redeemStart,
            redeemEnd,
        });
    };

    // 沿用選品頁同一套點數運算，唯讀顯示合計與剩餘點數
    const { usedPoints, remainingPoints, resetCart } = useRedeemCart(products, redeemablePoints);

    // 功能：守衛＋版面
    useEffect(() => {
        setHeaderType(HEADER_TYPE.DEFAULT);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DEFAULT);
        setHeaderContent(<RedeemStepper activeStep={1} onBack={handleBack} />);

        if (!location.state?.cart || !location.state?.products) history.replace(MEMBER_REDEEM_ROUTER);

        return () => setHeaderContent(null);
        // eslint-disable-next-line
    }, [history, setHeaderType, setFooterType, setThemeType, setHeaderContent]);

    // 功能：載入草稿（僅初始化一次）
    useEffect(() => {
        resetCart(cart);
        // eslint-disable-next-line
    }, []);

    if (!location.state?.cart || !location.state?.products) return null;

    const cartItems = products.filter((p) => (cart[p.itemID] || 0) > 0);

    // 功能：送出兌換，成功後導回列表頁
    const handleConfirm = async () => {
        const isSuccess = await postRedeemCartApi(toRedeemPayload(cart, products));
        if (isSuccess) history.replace(MEMBER_REDEEM_ROUTER);
        // 失敗：不導航，留在本頁（useAxios 既有錯誤提示負責回饋）
    };

    return (
        <Box className="redeem-confirm-page">
            <Box className="container">
                <Typography className="redeem-confirm-title">確認兌換</Typography>

                {/* 將兌換品項 */}
                <Box className="redeem-confirm-card">
                    {cartItems.map((item) => (
                        <Box key={item.itemID} className="redeem-confirm-item-row">
                            <Typography className="redeem-confirm-item-name">
                                {item.itemName} × {cart[item.itemID]}
                            </Typography>
                            <Typography className="redeem-confirm-item-subtotal">
                                {item.points * cart[item.itemID]} 點
                            </Typography>
                        </Box>
                    ))}
                    <Box className="redeem-confirm-total-row">
                        <Typography className="redeem-confirm-total-label">合計點數</Typography>
                        <Typography className="redeem-confirm-total-value">{usedPoints} 點</Typography>
                    </Box>
                </Box>

                <Box className="redeem-confirm-remain-row">
                    <Typography className="redeem-confirm-remain-label">兌換後剩餘點數</Typography>
                    <Typography className="redeem-confirm-remain-value">{Math.max(0, remainingPoints)} 點</Typography>
                </Box>

                {/* 操作按鈕 */}
                <Stack direction="column" spacing={1.5} className="redeem-confirm-actions">
                    <Button variant="contained" fullWidth onClick={handleConfirm} disabled={!canRedeem}>
                        確認兌換
                    </Button>
                    <Button variant="text" fullWidth onClick={handleBack}>
                        返回修改
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};

export default RedeemConfirmPage;
